import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import Imgix from 'shared/components/imgix';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { mediaQueries } from 'shared/styles';
import { ImgixDefaultSettings } from 'shared/constants';
import { tracker } from 'src/utils/analytics';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensarySpecials from 'src/dispensary/hooks/use-dispensary-specials';
import usePaths from 'src/hooks/use-paths';
import ROUTES from 'src/routes';
import { GqlImageBanner } from 'types/graphql';
import { BannerImageWrapper } from './banner-image-wrapper';

// Banner type string values (matches ImageBannerType enum from GraphQL schema)
const BANNER_TYPE = {
  link: 'link',
  special: 'special',
  collection: 'collection',
  category: 'category',
} as const;

/**
 * Computes the link URL and whether it's an internal route based on banner type
 * @param banner - The image banner data
 * @param specialType - The special type ('sale' or 'bogo') looked up from specials, used for routing
 * @param isSpecialLoading - Whether the special data is still loading
 */
function useBannerLink(
  banner: GqlImageBanner,
  specialType: string | undefined,
  isSpecialLoading: boolean
): { linkUrl: string | null; isInternalRoute: boolean } {
  const { href: baseHref } = usePaths();

  return useMemo(() => {
    const { bannerType, connectedId, link } = banner;

    // For link type or no bannerType, use the existing link behavior
    if (!bannerType || bannerType === BANNER_TYPE.link) {
      return { linkUrl: link ?? null, isInternalRoute: false };
    }

    // For connected banners, compute the internal route
    if (!connectedId) {
      return { linkUrl: null, isInternalRoute: false };
    }

    const baseUrl = String(baseHref || '');
    const specialsRoute = String(ROUTES.SPECIALS);
    const collectionsRoute = String(ROUTES.COLLECTIONS);
    const entityId = String(connectedId);

    switch (bannerType) {
      case BANNER_TYPE.special: {
        // Wait for special data to load before enabling the link
        // This prevents incorrect routing while specialType is unknown
        if (isSpecialLoading) {
          return { linkUrl: null, isInternalRoute: false };
        }
        // Route to /specials/sale/[id] for sale specials, /specials/offer/[id] for bogo/offer specials
        const specialTypeSubRoute = specialType === 'sale' ? 'sale' : 'offer';
        return {
          linkUrl: `${baseUrl}${specialsRoute}/${specialTypeSubRoute}/${entityId}`,
          isInternalRoute: true,
        };
      }
      case BANNER_TYPE.collection:
      case BANNER_TYPE.category:
        // Both collections and custom categories use /collections/[id] route
        return {
          linkUrl: `${baseUrl}${collectionsRoute}${entityId}`,
          isInternalRoute: true,
        };
      default:
        return { linkUrl: link ?? null, isInternalRoute: false };
    }
  }, [banner, baseHref, specialType, isSpecialLoading]);
}

const MenuImage = ({
  banner,
  index,
  fixedSize = true,
}: {
  banner: GqlImageBanner;
  index: number;
  fixedSize?: boolean;
}): JSX.Element => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down(`sm`));
  const { dispensary } = useDispensary();
  const flags = useFlags();

  // Look up the special type when the banner is connected to a special
  // Without the type, we can't route to the correct page
  // TODO: Consolidate this to a single route.
  const isSpecialBanner = banner.bannerType === BANNER_TYPE.special && banner.connectedId;
  const { specials, loading: specialsLoading } = useDispensarySpecials({
    specialId: isSpecialBanner ? banner.connectedId : undefined,
    skip: !isSpecialBanner,
  });

  // Find the connected special and get its type
  const connectedSpecial = useMemo(() => {
    if (!isSpecialBanner || specials.length === 0) {
      return null;
    }
    return specials.find((s: { _id: string }) => s._id === banner.connectedId) ?? null;
  }, [isSpecialBanner, specials, banner.connectedId]);

  const specialType = connectedSpecial?.specialType;
  // Only consider loading for special banners that need to fetch special data
  const isSpecialLoading = Boolean(isSpecialBanner && specialsLoading);
  const { linkUrl, isInternalRoute } = useBannerLink(banner, specialType, isSpecialLoading);

  const isAscendMenuStyleEnabled = flags['growth.ecomm.ascend-menu-style-hotfix.rollout'] ?? false;

  const loadingAttribute = index === 0 ? 'eager' : 'lazy';

  // Compute target attribute for links
  // For internal routes, don't open in new tab
  // For external links, respect the banner's openInNewTab setting
  const getTarget = (): string => {
    if (isInternalRoute) {
      return '_self';
    }
    const shouldOpenInNewTab =
      banner.openInNewTab === null || typeof banner.openInNewTab === 'undefined' || banner.openInNewTab;
    return shouldOpenInNewTab ? '_blank' : '_top';
  };
  const target = getTarget();
  const fixedAspectRatio = isMobile ? '2.22:1' : '3:1';

  return (
    <BannerImageWrapper
      linkUrl={linkUrl}
      isInternalRoute={isInternalRoute}
      rel={isInternalRoute ? undefined : 'noopener noreferrer'}
      target={target}
      onClick={() => tracker.imageBannerClicked({ dispensaryId: dispensary.id, dispensaryName: dispensary.name })}
    >
      <MainImage
        type='img'
        fixedSize={fixedSize}
        imgixParams={{
          ...ImgixDefaultSettings,
          crop: [`edges`],
          ar: fixedSize ? fixedAspectRatio : undefined,
          auto: 'compress',
          fm: 'webp',
          fill: 'blur',
        }}
        width={fixedSize ? 1200 : undefined}
        height={fixedSize ? 400 : undefined}
        htmlAttributes={{ alt: banner.alt, loading: loadingAttribute }}
        src={isMobile ? banner.mobileImage : banner.image}
        className={`product-image-${index}`}
        isAscendMenuStyleEnabled={isAscendMenuStyleEnabled}
      />
    </BannerImageWrapper>
  );
};

export default MenuImage;

const MainImage = styled(Imgix)<{ fixedSize: boolean; isAscendMenuStyleEnabled: boolean }>`
  ${({ fixedSize }) =>
    fixedSize &&
    css`
      aspect-ratio: 3/1 auto;
    `}
  border-radius: var(--dct--customized-radius-lg);
  overflow: hidden;
  display: block;
  object-fit: contain;
  width: 100%;

  @media ${mediaQueries.largePhone} {
    ${({ fixedSize }) =>
      fixedSize &&
      css`
        aspect-ratio: 2.22/1 auto;
      `}
    width: 100%;
    height: auto;
  }
`;
